import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isHomePage = location.pathname === '/';

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    closeMenu();
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navLinks = isHomePage ? (
    <>
      <li>
        <a
          href="#hero"
          onClick={closeMenu}
          className="nav-link-item"
        >
          Início
        </a>
      </li>
      <li>
        <a
          href="#products"
          onClick={closeMenu}
          className="nav-link-item"
        >
          Produtos/Serviços
        </a>
      </li>
      <li>
        <a
          href="#about"
          onClick={closeMenu}
          className="nav-link-item"
        >
          Sobre
        </a>
      </li>
      <li>
        <Link
          to="/faq"
          onClick={closeMenu}
          className="nav-link-item"
        >
          FAQ
        </Link>
      </li>
      <li>
        <a
          href="#contact"
          onClick={closeMenu}
          className="nav-link-item"
        >
          Contacto
        </a>
      </li>
    </>
  ) : (
    <>
      <li>
        <Link
          to="/"
          onClick={closeMenu}
          className="nav-link-item"
        >
          Início
        </Link>
      </li>
      <li>
        <Link
          to="/#products"
          onClick={closeMenu}
          className="nav-link-item"
        >
          Produtos/Serviços
        </Link>
      </li>
      <li>
        <Link
          to="/#about"
          onClick={closeMenu}
          className="nav-link-item"
        >
          Sobre
        </Link>
      </li>
      <li>
        <Link
          to="/faq"
          onClick={closeMenu}
          className="nav-link-item"
        >
          FAQ
        </Link>
      </li>
      <li>
        <Link
          to="/#contact"
          onClick={closeMenu}
          className="nav-link-item"
        >
          Contacto
        </Link>
      </li>
    </>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-md'
          : 'bg-white shadow-sm'
      }`}
    >
      <nav className="navbar w-full px-8 sm:px-12 lg:px-16 xl:px-24 py-3.5 flex items-center justify-between gap-12">
        {/* Logo */}
        <Link
          to="/"
          className="logo-link group focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2 rounded-lg flex-shrink-0"
        >
          <motion.h1
            className="logo text-2xl lg:text-2xl font-bold bg-gradient-to-br from-primary-600 via-primary-500 to-primary-600 bg-clip-text text-transparent group-hover:from-primary-700 group-hover:via-primary-600 group-hover:to-primary-700 transition-all duration-200"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Vulpes
          </motion.h1>
        </Link>

        {/* Desktop Navigation - Centered */}
        <ul id="desktop-nav" className="hidden md:flex items-center space-x-8 lg:space-x-10 flex-1 justify-center">
          {navLinks}
        </ul>

        {/* Right side actions */}
        <div className="flex items-center gap-6 flex-shrink-0">
          {/* Login Button */}
          <motion.a
            href="https://cadernos.vulpes.pt/"
            className="hidden md:inline-flex items-center gap-2 text-gray-700 hover:text-primary-600 font-semibold text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2 rounded-lg px-3 py-1.5"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <span>Entrar</span>
          </motion.a>

          {/* Mobile Menu Toggle */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 text-gray-700 hover:text-primary-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2 rounded-lg transition-all duration-200"
            aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={isMenuOpen}
          >
            <motion.div
              animate={isMenuOpen ? 'open' : 'closed'}
              className="w-6 h-5 flex flex-col justify-between"
            >
              <motion.span
                variants={{
                  closed: { rotate: 0, y: 0 },
                  open: { rotate: 45, y: 8 },
                }}
                className="w-full h-0.5 bg-current transition-all"
              />
              <motion.span
                variants={{
                  closed: { opacity: 1 },
                  open: { opacity: 0 },
                }}
                className="w-full h-0.5 bg-current transition-all"
              />
              <motion.span
                variants={{
                  closed: { rotate: 0, y: 0 },
                  open: { rotate: -45, y: -8 },
                }}
                className="w-full h-0.5 bg-current transition-all"
              />
            </motion.div>
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeMenu}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm md:hidden"
                style={{ top: '72px' }}
              />

              {/* Menu Panel */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="fixed right-0 top-[72px] bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl md:hidden overflow-y-auto"
              >
                <ul className="flex flex-col p-6 space-y-1">
                  {navLinks}

                  {/* Mobile Login Button */}
                  <li className="pt-6 mt-4 border-t border-gray-200">
                    <a
                      href="https://cadernos.vulpes.pt/"
                      className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white px-5 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeMenu}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                        />
                      </svg>
                      <span>Entrar na Plataforma</span>
                    </a>
                  </li>
                </ul>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>

      <style>{`
        /* Override old App.css styles */
        #desktop-nav {
          display: none !important;
        }

        @media (min-width: 768px) {
          #desktop-nav {
            display: flex !important;
            flex-direction: row !important;
            list-style: none !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }

        .nav-link-item {
          @apply block py-3 px-4 text-gray-700 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2 rounded-lg md:py-2 md:px-4;
          display: block !important;
          color: #374151 !important;
          text-decoration: none !important;
          border-bottom: none !important;
          letter-spacing: 0.01em;
          position: relative;
          font-weight: 500;
          font-size: 0.875rem;
        }

        .nav-link-item:hover {
          color: rgb(201, 79, 30) !important;
          border-bottom: none !important;
          background-color: rgba(201, 79, 30, 0.05);
        }

        .nav-link-item:focus {
          color: rgb(201, 79, 30) !important;
          border-bottom: none !important;
        }

        .nav-link-item:active {
          color: rgb(180, 70, 25) !important;
        }
      `}</style>
    </header>
  );
};

export default Navbar;
